import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './OnboardingWizard.css';

export default function OnboardingWizard({ onComplete }) {
  const { t } = useTranslation();
  const username = localStorage.getItem('userName') || localStorage.getItem('username') || 'user';
  const storageKey = `onboardingCompleted_${username}`;

  // Static fallback; replaced by the live catalog from GET /frameworks so new
  // frameworks (e.g. SOC 1) appear without code changes.
  const FRAMEWORKS_FALLBACK = [
    { id: 'SOC2',      standard: 'soc2',     label: 'SOC 2',       icon: 'fa-shield-alt',    sub: t('OnboardingWizard.frameworkSoc2Sub') },
    { id: 'ISO27001',  standard: 'iso27001', label: 'ISO 27001',   icon: 'fa-globe',          sub: t('OnboardingWizard.frameworkIso27001Sub') },
    { id: 'HIPAA',     label: 'HIPAA',       icon: 'fa-heartbeat',      sub: t('OnboardingWizard.frameworkHipaaSub') },
    { id: 'PCI_DSS',   label: 'PCI-DSS',     icon: 'fa-credit-card',    sub: t('OnboardingWizard.frameworkPciDssSub') },
  ];
  const FW_ICONS = { soc1: 'fa-file-invoice-dollar', soc2: 'fa-shield-alt', iso27001: 'fa-globe',
    hipaa: 'fa-heartbeat', pci_dss: 'fa-credit-card', gdpr: 'fa-user-shield' };
  const [FRAMEWORKS, setFrameworksList] = useState(FRAMEWORKS_FALLBACK);
  useEffect(() => {
    axiosInstance.get('/frameworks')
      .then(r => {
        const list = (r.data || []).map(f => ({
          id: String(f.id),
          standard: (f.standard || '').toLowerCase(),
          label: f.displayName || f.name || f.shortName,
          icon: FW_ICONS[(f.standard || '').toLowerCase()] || 'fa-clipboard-check',
          sub: f.shortName || '',
        }));
        if (list.length) setFrameworksList(list);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TSC_CRITERIA = [
    { id: 'security',        label: t('OnboardingWizard.criteriaSecurityLabel'),        required: true,  sub: t('OnboardingWizard.criteriaSecuritySub') },
    { id: 'availability',    label: t('OnboardingWizard.criteriaAvailabilityLabel'),    required: false, sub: t('OnboardingWizard.criteriaAvailabilitySub') },
    { id: 'confidentiality', label: t('OnboardingWizard.criteriaConfidentialityLabel'), required: false, sub: t('OnboardingWizard.criteriaConfidentialitySub') },
    { id: 'pi',              label: t('OnboardingWizard.criteriaProcessingIntegrityLabel'), required: false, sub: t('OnboardingWizard.criteriaProcessingIntegritySub') },
    { id: 'privacy',         label: t('OnboardingWizard.criteriaPrivacyLabel'),         required: false, sub: t('OnboardingWizard.criteriaPrivacySub') },
  ];

  const INTEGRATIONS = [
    { id: 'aws',    label: 'AWS',              icon: 'fa-cloud',      color: '#FF9900', sub: t('OnboardingWizard.integrationAwsSub') },
    { id: 'github', label: 'GitHub',           icon: 'fa-code-branch',color: '#7c3aed', sub: t('OnboardingWizard.integrationGithubSub') },
    { id: 'google', label: 'Google Workspace', icon: 'fa-google',     color: '#4285F4', sub: t('OnboardingWizard.integrationGoogleSub') },
    { id: 'okta',   label: 'Okta',             icon: 'fa-id-badge',   color: '#007DC1', sub: t('OnboardingWizard.integrationOktaSub') },
    { id: 'slack',  label: 'Slack',            icon: 'fa-slack',      color: '#4A154B', sub: t('OnboardingWizard.integrationSlackSub') },
    { id: 'jira',   label: 'Jira',             icon: 'fa-jira',       color: '#0052CC', sub: t('OnboardingWizard.integrationJiraSub') },
  ];

  const SCAN_STEPS = [
    t('OnboardingWizard.scanStepIdentity'),
    t('OnboardingWizard.scanStepAssets'),
    t('OnboardingWizard.scanStepRepos'),
    t('OnboardingWizard.scanStepAccess'),
    t('OnboardingWizard.scanStepBaseline'),
  ];

  const [step,         setStep]         = useState(1);
  const [framework,    setFramework]    = useState('SOC2');
  const [scope,        setScope]        = useState(['security']);
  const [connected,    setConnected]    = useState([]);
  const [useDemoData,  setUseDemoData]  = useState(false);
  const [scanStep,     setScanStep]     = useState(0);
  const [scanDone,     setScanDone]     = useState(false);
  const [score,        setScore]        = useState(null);

  // Trust Services Criteria are a SOC 2 concept only. Other frameworks must not be asked for them.
  const selectedFw  = FRAMEWORKS.find(f => f.id === framework);
  const isSoc2Fw    = selectedFw ? selectedFw.standard === 'soc2' : framework === 'SOC2';
  const isIsoFw     = selectedFw ? selectedFw.standard === 'iso27001' : framework === 'ISO27001';
  const fwLabel     = selectedFw ? selectedFw.label : framework;

  // Step 4 — seed demo data (if opted in) then animate
  useEffect(() => {
    if (step !== 4) return;
    const runScan = async () => {
      if (useDemoData) {
        try { await axiosInstance.post('/demo/seed'); } catch (_) {}
      }
      let i = 0;
      const tick = setInterval(() => {
        i++;
        setScanStep(i);
        if (i >= SCAN_STEPS.length) {
          clearInterval(tick);
          setTimeout(() => setScanDone(true), 600);
        }
      }, 900);
      return () => clearInterval(tick);
    };
    runScan();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 4 → 5 transition after scan
  useEffect(() => {
    if (!scanDone) return;
    axiosInstance.get('/audit/readiness-score')
      .then(r => setScore(r.data.score))
      .catch(() => setScore(42))
      .finally(() => setTimeout(() => setStep(5), 500));
  }, [scanDone]);

  const toggleScope = (id) => {
    if (id === 'security') return;
    setScope(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const toggleConnect = (id) => {
    const next = connected.includes(id) ? connected.filter(c => c !== id) : [...connected, id];
    setConnected(next);
    setUseDemoData(next.length > 0);
  };

  const finish = () => {
    localStorage.setItem(storageKey, 'true');
    if (onComplete) onComplete();
  };

  const scoreColor = score >= 80 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';
  const scoreLabel = score >= 80 ? t('OnboardingWizard.scoreLabelLookingGood') : score >= 60 ? t('OnboardingWizard.scoreLabelOnTrack') : t('OnboardingWizard.scoreLabelNeedsWork');

  return (
    <div className="ow-overlay">
      <div className="ow-modal">
        {/* Progress bar */}
        <div className="ow-progress">
          {[1,2,3,4,5].map(s => (
            <div key={s} className={`ow-prog-step ${step >= s ? 'done' : ''} ${step === s ? 'active' : ''}`}>
              <div className="ow-prog-dot">{step > s ? <i className="fas fa-check" /> : s}</div>
              <div className="ow-prog-label">
                {s === 1 ? t('OnboardingWizard.progressFramework') : s === 2 ? t('OnboardingWizard.progressScope') : s === 3 ? t('OnboardingWizard.progressIntegrations') : s === 4 ? t('OnboardingWizard.progressScanning') : t('OnboardingWizard.progressBaseline')}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="ow-body">
            <div className="ow-heading">{t('OnboardingWizard.step1Heading')}</div>
            <div className="ow-sub">{t('OnboardingWizard.step1Sub')}</div>
            <div className="ow-framework-grid">
              {FRAMEWORKS.map(f => (
                <button key={f.id}
                        className={`ow-fw-card ${framework === f.id ? 'selected' : ''}`}
                        onClick={() => setFramework(f.id)}>
                  <i className={`fas ${f.icon} ow-fw-icon`} />
                  <div className="ow-fw-label">{f.label}</div>
                  <div className="ow-fw-sub">{f.sub}</div>
                  {framework === f.id && <div className="ow-fw-check"><i className="fas fa-check-circle" /></div>}
                </button>
              ))}
            </div>
            <div className="ow-actions">
              <button className="ow-skip" onClick={finish}>{t('OnboardingWizard.skipSetup')}</button>
              <button className="ow-next" onClick={() => setStep(2)}>{t('OnboardingWizard.next')} <i className="fas fa-arrow-right" /></button>
            </div>

          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="ow-body">
            {isSoc2Fw ? (
              <>
                <div className="ow-heading">{t('OnboardingWizard.step2Heading')}</div>
                <div className="ow-sub">{t('OnboardingWizard.step2Sub', { framework: 'SOC 2' })}</div>
                <div className="ow-scope-list">
                  {TSC_CRITERIA.map(c => (
                    <label key={c.id} className={`ow-scope-item ${scope.includes(c.id) ? 'checked' : ''} ${c.required ? 'required' : ''}`}>
                      <input type="checkbox" checked={scope.includes(c.id)} onChange={() => toggleScope(c.id)} disabled={c.required} />
                      <div className="ow-scope-info">
                        <div className="ow-scope-label">
                          {c.label}
                          {c.required && <span className="ow-required-badge">{t('OnboardingWizard.requiredBadge')}</span>}
                        </div>
                        <div className="ow-scope-sub">{c.sub}</div>
                      </div>
                      {scope.includes(c.id) && <i className="fas fa-check-circle ow-scope-check" />}
                    </label>
                  ))}
                </div>
              </>
            ) : isIsoFw ? (
              <>
                <div className="ow-heading">{t('OnboardingWizard.step2IsoHeading', 'Plan your ISMS scope')}</div>
                <div className="ow-sub">{t('OnboardingWizard.step2IsoSub', 'ISO 27001 does not use trust criteria. You define the scope of your information security management system (ISMS), then decide which Annex A controls apply. You will do this in the ISMS workspace after setup:')}</div>
                <div className="ow-scope-list">
                  {[
                    ['fa-user-tie', t('OnboardingWizard.step2IsoOwner', 'Appoint an ISMS owner'), t('OnboardingWizard.step2IsoOwnerSub', 'A named compliance officer or security lead accountable for the ISMS.')],
                    ['fa-draw-polygon', t('OnboardingWizard.step2IsoScope', 'Define the scope'), t('OnboardingWizard.step2IsoScopeSub', 'Company-wide or a single product, and the systems and locations in it.')],
                    ['fa-exclamation-triangle', t('OnboardingWizard.step2IsoRisk', 'Build the risk register'), t('OnboardingWizard.step2IsoRiskSub', 'Identify and score risks by likelihood and impact, and record treatment.')],
                    ['fa-clipboard-list', t('OnboardingWizard.step2IsoSoa', 'Prepare the Statement of Applicability'), t('OnboardingWizard.step2IsoSoaSub', 'Mark each Annex A control applicable or not, with a reason.')],
                  ].map(([icon, label, sub]) => (
                    <div key={label} className="ow-scope-item checked">
                      <i className={`fas ${icon}`} />
                      <div className="ow-scope-info">
                        <div className="ow-scope-label">{label}</div>
                        <div className="ow-scope-sub">{sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="ow-heading">{t('OnboardingWizard.step2OtherHeading', 'Scope')}</div>
                <div className="ow-sub">{t('OnboardingWizard.step2OtherSub', 'You will review the controls that apply to {{framework}} and set your scope after setup, under Frameworks.', { framework: fwLabel })}</div>
              </>
            )}
            <div className="ow-actions">
              <button className="ow-back" onClick={() => setStep(1)}><i className="fas fa-arrow-left" /> {t('OnboardingWizard.back')}</button>
              <button className="ow-next" onClick={() => setStep(3)}>{t('OnboardingWizard.next')} <i className="fas fa-arrow-right" /></button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="ow-body">
            <div className="ow-heading">{t('OnboardingWizard.step3Heading')}</div>
            <div className="ow-sub">{t('OnboardingWizard.step3Sub')}</div>
            {!useDemoData && (
              <div className="ow-demo-hint">
                <i className="fas fa-flask" /> {t('OnboardingWizard.demoHintBefore')} <strong>{t('OnboardingWizard.demoHintEmphasis')}</strong> {t('OnboardingWizard.demoHintAfter')}
              </div>
            )}
            {useDemoData && (
              <div className="ow-demo-active">
                <i className="fas fa-check-circle" /> {t('OnboardingWizard.demoActive')}
              </div>
            )}
            <div className="ow-int-grid">
              {INTEGRATIONS.map(int => (
                <div key={int.id}
                     className={`ow-int-card ${connected.includes(int.id) ? 'connected' : ''}`}
                     onClick={() => toggleConnect(int.id)}>
                  <i className={`fab ${int.icon} ow-int-icon`} style={{ color: int.color }} />
                  <div className="ow-int-label">{int.label}</div>
                  <div className="ow-int-sub">{int.sub}</div>
                  <div className="ow-int-btn">
                    {connected.includes(int.id)
                      ? <span className="ow-int-connected"><i className="fas fa-check" /> {t('OnboardingWizard.integrationSelected')}</span>
                      : <span className="ow-int-demo">{t('OnboardingWizard.integrationAdd')}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="ow-actions">
              <button className="ow-back" onClick={() => setStep(2)}><i className="fas fa-arrow-left" /> {t('OnboardingWizard.back')}</button>
              <button className="ow-next" onClick={() => setStep(4)}>
                {useDemoData ? t('OnboardingWizard.loadDemoAndScan') : t('OnboardingWizard.scanEnvironment')} <i className="fas fa-arrow-right" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="ow-body ow-scan-body">
            <div className="ow-heading">{t('OnboardingWizard.step4Heading')}</div>
            <div className="ow-sub">{t('OnboardingWizard.step4Sub')}</div>
            <div className="ow-scan-anim">
              <div className="ow-scan-ring" />
              <div className="ow-scan-ring ow-ring2" />
              <i className="fas fa-shield-alt ow-scan-icon" />
            </div>
            <div className="ow-scan-steps">
              {SCAN_STEPS.map((s, i) => (
                <div key={i} className={`ow-scan-step ${i < scanStep ? 'done' : i === scanStep - 1 ? 'active' : ''}`}>
                  {i < scanStep
                    ? <i className="fas fa-check-circle ow-ss-icon done" />
                    : i === scanStep - 1
                    ? <i className="fas fa-spinner fa-spin ow-ss-icon active" />
                    : <i className="fas fa-circle ow-ss-icon pending" />}
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 5 */}
        {step === 5 && score !== null && (
          <div className="ow-body">
            <div className="ow-heading">{t('OnboardingWizard.step5Heading')}</div>
            <div className="ow-sub">{t('OnboardingWizard.step5Sub')}</div>

            <div className="ow-baseline-card">
              <div className="ow-score-wrap">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke={scoreColor} strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 50 * score / 100} ${2 * Math.PI * 50 * (1 - score / 100)}`}
                    strokeDashoffset={2 * Math.PI * 50 * 0.25}
                    strokeLinecap="round" />
                </svg>
                <div className="ow-score-inner">
                  <div className="ow-score-num" style={{ color: scoreColor }}>{score}%</div>
                  <div className="ow-score-lbl">{scoreLabel}</div>
                </div>
              </div>
              <div className="ow-baseline-desc">
                <div className="ow-bl-title">{t('OnboardingWizard.readinessScoreTitle')}</div>
                <div className="ow-bl-sub">
                  {t('OnboardingWizard.baselineDescBase')}
                  {score < 60 && t('OnboardingWizard.baselineDescLow')}
                  {score >= 60 && score < 80 && t('OnboardingWizard.baselineDescMid')}
                  {score >= 80 && t('OnboardingWizard.baselineDescHigh')}
                </div>
              </div>
            </div>

            <div className="ow-gaps">
              <div className="ow-gaps-title">{t('OnboardingWizard.gapsTitle')}</div>
              {[
                { icon: 'fa-mobile-alt', label: t('OnboardingWizard.gapMfa'),      tag: 'CC6.1' },
                { icon: 'fa-code-branch', label: t('OnboardingWizard.gapBranchProtection'), tag: 'CC8.1' },
                { icon: 'fa-file-signature', label: t('OnboardingWizard.gapPolicies'),        tag: 'CC1.4' },
                { icon: 'fa-user-check', label: t('OnboardingWizard.gapAccessReview'),            tag: 'CC6.2' },
                { icon: 'fa-shield-alt', label: t('OnboardingWizard.gapS3Public'),   tag: 'CC6.6' },
              ].map((g, i) => (
                <div key={i} className="ow-gap-item">
                  <i className={`fas ${g.icon} ow-gap-icon`} />
                  <span className="ow-gap-label">{g.label}</span>
                  <span className="ow-gap-tag">{g.tag}</span>
                </div>
              ))}
            </div>

            <div className="ow-actions">
              <button className="ow-next ow-finish" onClick={finish}>
                {t('OnboardingWizard.goToDashboard')} <i className="fas fa-arrow-right" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
